/**
 * Notification Sidebar — live notification bell + panel for admin staff.
 * Polls every 15 seconds for near-real-time updates.
 */
import { api, router, timeAgo } from '@isg/ui-kit';
import { t } from '../i18n/index.js';

var pollInterval = null;
var isOpen = false;
var sidebarEl = null;

var BELL_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>';

function esc(str) {
  var d = document.createElement('div');
  d.textContent = str || '';
  return d.innerHTML;
}

function truncate(str, max) {
  if (!str) return '';
  return str.length <= max ? str : str.substring(0, max) + '...';
}

// Relative times come from ui-kit's timeAgo(), which follows the active locale.

// Returns a ui-kit chrome key; translated where the label is rendered.
function getTimeGroup(isoStr) {
  var now = new Date();
  var date = new Date(isoStr);
  var diffDays = Math.floor((now - date) / 86400000);
  if (diffDays === 0) return 'notifications.group_today';
  if (diffDays === 1) return 'notifications.group_yesterday';
  if (diffDays < 7) return 'notifications.group_week';
  return 'notifications.group_older';
}

function getIcon(type) {
  var icons = {
    cert_uploaded: '\uD83D\uDCC4',
    cert_approved: '\u2705',
    cert_rejected: '\u274C',
    cert_expiring: '\u26A0\uFE0F',
    cert_expired: '\uD83D\uDD34',
    cert_overdue: '\uD83D\uDD34',
    system: '\u2699\uFE0F',
  };
  return icons[type] || '\uD83D\uDD14';
}

export function createNotificationBell() {
  var btn = document.createElement('button');
  btn.className = 'toolbar-notification-btn';
  btn.setAttribute('aria-label', t('notifications.title'));
  btn.innerHTML = BELL_SVG + '<span class="notification-badge" style="display:none;"></span>';

  btn.addEventListener('click', function(e) {
    e.stopPropagation();
    toggleSidebar();
  });

  pollUnread();
  pollInterval = setInterval(pollUnread, 15000);

  return btn;
}

export function createNotificationSidebar() {
  var sidebar = document.createElement('aside');
  sidebar.className = 'notification-sidebar';
  sidebar.id = 'notification-sidebar';

  sidebar.innerHTML = [
    '<div class="notification-sidebar-header">',
    '  <h2>' + t('notifications.title') + '</h2>',
    '  <div class="notification-sidebar-actions">',
    '    <button class="btn-ghost-sm" id="notif-mark-all">' + t('notifications.mark_all_read') + '</button>',
    '    <button class="notification-sidebar-close" id="notif-close" aria-label="' + t('notifications.close') + '">',
    '      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    '    </button>',
    '  </div>',
    '</div>',
    '<div class="notification-sidebar-tabs">',
    '  <button class="notification-tab active" data-filter="all">' + t('notifications.all') + '</button>',
    '  <button class="notification-tab" data-filter="unread">' + t('notifications.unread') + '</button>',
    '</div>',
    '<div class="notification-sidebar-list" id="notif-list">',
    '  <div style="padding:var(--space-6);text-align:center;"><div class="spinner" style="margin:0 auto;"></div></div>',
    '</div>',
  ].join('\n');

  sidebarEl = sidebar;

  sidebar.querySelector('#notif-close').addEventListener('click', closeSidebar);

  sidebar.querySelector('#notif-mark-all').addEventListener('click', async function() {
    try {
      await api.post('/notifications/read-all');
      updateBadge(0);
      sidebar.querySelectorAll('.notif-item.unread').forEach(function(el) {
        el.classList.remove('unread');
      });
    } catch (e) { /* silent */ }
  });

  sidebar.querySelectorAll('.notification-tab').forEach(function(tab) {
    tab.addEventListener('click', function() {
      sidebar.querySelectorAll('.notification-tab').forEach(function(tb) { tb.classList.remove('active'); });
      tab.classList.add('active');
      loadNotifications(tab.dataset.filter);
    });
  });

  sidebar.querySelector('#notif-list').addEventListener('click', async function(e) {
    var item = e.target.closest('.notif-item');
    if (!item) return;

    var notifId = item.dataset.id;
    var url = item.dataset.url;

    if (item.classList.contains('unread')) {
      try {
        await api.post('/notifications/' + notifId + '/read');
        item.classList.remove('unread');
        var badge = document.querySelector('.notification-badge');
        var current = parseInt(badge?.textContent) || 0;
        updateBadge(Math.max(0, current - 1));
      } catch (e) { /* silent */ }
    }

    if (url) {
      closeSidebar();
      router.navigate(url);
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && isOpen) closeSidebar();
  });

  return sidebar;
}

function toggleSidebar() {
  if (isOpen) closeSidebar();
  else openSidebar();
}

function openSidebar() {
  isOpen = true;
  if (sidebarEl) {
    sidebarEl.classList.add('open');
    loadNotifications('all');
  }
}

function closeSidebar() {
  isOpen = false;
  if (sidebarEl) sidebarEl.classList.remove('open');
}

async function loadNotifications(filter) {
  filter = filter || 'all';
  var listEl = sidebarEl?.querySelector('#notif-list');
  if (!listEl) return;

  listEl.innerHTML = '<div style="padding:var(--space-6);text-align:center;"><div class="spinner" style="margin:0 auto;"></div></div>';

  try {
    var unreadOnly = filter === 'unread' ? '&unread=true' : '';
    var res = await api.get('/notifications?per_page=30' + unreadOnly);
    var notifications = res.notifications || [];

    updateBadge(res.unread_count || 0);

    if (!notifications.length) {
      listEl.innerHTML = '<div class="notification-empty"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-quaternary)" stroke-width="1.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg><p>' + t('notif.empty') + '</p></div>';
      return;
    }

    var groups = {};
    for (var i = 0; i < notifications.length; i++) {
      var n = notifications[i];
      var group = getTimeGroup(n.created_at);
      if (!groups[group]) groups[group] = [];
      groups[group].push(n);
    }

    var html = '';
    var groupNames = Object.keys(groups);
    for (var g = 0; g < groupNames.length; g++) {
      var groupName = groupNames[g];
      var items = groups[groupName];
      html += '<div class="notif-group-label">' + t(groupName) + '</div>';
      for (var j = 0; j < items.length; j++) {
        var item = items[j];
        var unreadClass = item.is_read ? '' : ' unread';
        html += '<div class="notif-item' + unreadClass + '" data-id="' + item.id + '" data-url="' + esc(item.url || '') + '">' +
          '<div class="notif-icon">' + getIcon(item.type) + '</div>' +
          '<div class="notif-content">' +
            '<div class="notif-title">' + esc(item.title) + '</div>' +
            (item.body ? '<div class="notif-body">' + esc(truncate(item.body, 100)) + '</div>' : '') +
            '<div class="notif-time">' + timeAgo(item.created_at) + '</div>' +
          '</div>' +
          (!item.is_read ? '<div class="notif-unread-dot"></div>' : '') +
        '</div>';
      }
    }

    listEl.innerHTML = html;
  } catch (err) {
    listEl.innerHTML = '<div style="padding:var(--space-6);text-align:center;color:var(--text-tertiary);font-size:var(--text-sm);">' + t('notifications.load_failed') + '</div>';
  }
}

function updateBadge(count) {
  var badges = document.querySelectorAll('.notification-badge');
  for (var i = 0; i < badges.length; i++) {
    if (count > 0) {
      badges[i].style.display = '';
      badges[i].textContent = count > 99 ? '99+' : String(count);
    } else {
      badges[i].style.display = 'none';
    }
  }
}

async function pollUnread() {
  try {
    var res = await api.get('/notifications/unread-count');
    updateBadge(res.unread_count || 0);
  } catch (e) { /* silent */ }
}

export function destroyNotificationSidebar() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
  isOpen = false;
  sidebarEl = null;
}
